import { useState } from 'react';
import { Box, Button, List, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import FileUploadModal from './FileUploadModal';
import { AuditorCompetenceDetail, FileItem } from '@/common';
import { deleteFileFromAuditorCompetence } from '../../api/deleteFileFromAuditorCompetence';
import { downloadFileFromResponse, getMessageFromError } from '@/core';
import { downloadFileFromAuditorCompetence } from '../../api/downloadFileFromAuditorCompetence';

type FilesProps = {
  auditorCompetenceDetail: AuditorCompetenceDetail;
};

const Files = ({ auditorCompetenceDetail }: FilesProps) => {
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const isFormEditable =
    auditorCompetenceDetail?.isEditableOnPortal === undefined ||
    auditorCompetenceDetail?.isEditableOnPortal === true;

  const downloadFileMutation = useMutation({
    mutationFn: downloadFileFromAuditorCompetence,
    onSuccess: async (response) => {
      await downloadFileFromResponse(response);
      enqueueSnackbar(
        t('Successfully downloaded file.', { defaultValue: 'Successfully downloaded file.' }),
        { variant: 'success' }
      );
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  const deleteFileMutation = useMutation({
    mutationFn: deleteFileFromAuditorCompetence,
    onSuccess: () => {
      enqueueSnackbar(
        t('Successfully deleted file.', { defaultValue: 'Successfully deleted file.' }),
        { variant: 'success' }
      );
      queryClient.invalidateQueries(['auditor-competences-id', auditorCompetenceDetail.id]);
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  return (
    <>
      <FileUploadModal
        show={showFileUploadModal}
        setShow={setShowFileUploadModal}
        auditorCompetenceDetail={auditorCompetenceDetail}
      />

      <Box>
        {auditorCompetenceDetail.documents.length > 0 ? (
          <List>
            {auditorCompetenceDetail.documents.map((item) => (
              <FileItem
                key={item.id}
                name={item.title || ''}
                uploadedBy={item.uploadedBy}
                uploadedAt={item.uploadedAt}
                storeType={item.storeType}
                fileName={item.fileName}
                onDownload={() =>
                  downloadFileMutation.mutate({ id: auditorCompetenceDetail.id, fileId: item.id })
                }
                onDelete={
                  isFormEditable
                    ? () => {
                        confirm({
                          description: `${t('This will premamently delete', {
                            defaultValue: 'This will premamently delete',
                          })} ${item.title}`,
                        }).then(() =>
                          deleteFileMutation.mutate({
                            id: auditorCompetenceDetail.id,
                            fileId: item.id,
                          })
                        );
                      }
                    : undefined
                }
              />
            ))}
          </List>
        ) : (
          <Typography variant="subtitle1">{t('No files', { defaultValue: 'No files' })}</Typography>
        )}
      </Box>
      {isFormEditable && (
        <Button
          onClick={() => setShowFileUploadModal(true)}
          variant="outlined"
          startIcon={<AddIcon />}
          fullWidth
        >
          {t('Add files', { defaultValue: 'Add files' })}
        </Button>
      )}
    </>
  );
};

export default Files;
